import React, { useState, useEffect } from 'react';
import PageLayout from '../components/PageLayout';
import { sendContactEmail, fetchFormToken } from '../utils/sendEmail';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    email: '',
    contact: '',
    message: ''
  });
  
  const [phoneValue, setPhoneValue] = useState();
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [formError, setFormError] = useState('');

  // Anti-spam: hidden field humans never see, and a signed token proving the
  // page was actually loaded. Both are checked server-side.
  const [honeypot, setHoneypot] = useState('');
  const [formToken, setFormToken] = useState(null);

  useEffect(() => {
    let active = true;
    fetchFormToken().then((token) => {
      if (active) setFormToken(token);
    });
    return () => { active = false; };
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handlePhoneChange = (value) => {
    setPhoneValue(value);
    setFormData({
      ...formData,
      contact: value || ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!formData.email) {
      setFormError('Please enter your email address.');
      return;
    }
    if (!formData.contact || !isValidPhoneNumber(formData.contact)) {
      setFormError('Please enter a valid contact number for the country selected.');
      return;
    }
    if (!formData.message.trim()) {
      setFormError('Please enter a message.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const result = await sendContactEmail(formData, formToken, honeypot);

      if (result.success) {
        setSubmitStatus('success');

        // Reset form
        setFormData({
          email: '',
          contact: '',
          message: ''
        });
        setPhoneValue();

        // Tokens are single-use, so get a fresh one for any further enquiry.
        fetchFormToken().then(setFormToken);
      } else {
        setSubmitStatus('error');
        setFormError(result.error || 'Failed to send message. Please try again.');
      }
    } catch (error) {
      setSubmitStatus('error');
      setFormError('An error occurred. Please try again.');
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageLayout 
      title="Connect with Us" 
      subtitle="Get in touch to discuss your investment opportunities and business ventures"
    >
      <section className="contact-section">
        <div className="container">
          <div className="contact-content">
            <div className="contact-intro">
              <h2>Start Your Journey</h2>
              <p>
                Whether you're looking to invest in innovative technologies, seek funding for your startup, 
                or explore partnership opportunities, we're here to help. Reach out to us and let's discuss 
                how we can work together to achieve exceptional results.
              </p>
            </div>
            
            <div className="contact-form-wrapper">
              <form onSubmit={handleSubmit} className="contact-form">
                {/* Honeypot: off-screen and skipped by keyboard/screen readers,
                    so only an automated script will ever fill it in. */}
                <div className="form-hp" aria-hidden="true">
                  <label htmlFor="website">Website (leave blank)</label>
                  <input
                    type="text"
                    id="website"
                    name="website"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </div>

                <div className="form-group">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Your Email Address"
                    className="contact-form-input"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <PhoneInput
                    placeholder="Contact Number"
                    value={phoneValue}
                    onChange={handlePhoneChange}
                    defaultCountry="AE"
                    className="contact-phone-input"
                    international
                    countryCallingCodeEditable={false}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your business ideas, investment goals, or any questions you have..."
                    className="contact-form-textarea"
                    rows="6"
                    required
                  />
                </div>

                <div className="form-group">
                  <button
                    type="submit"
                    className="btn btn-submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                    <span className="contact-btn-arrow">→</span>
                  </button>
                </div>

                {submitStatus === 'success' && (
                  <p className="form-success-notice">
                    Thank you for your message. We will be in touch shortly.
                  </p>
                )}

                {formError && (
                  <p className="form-error-notice">{formError}</p>
                )}
              </form>
            </div>
            
            <div className="contact-info">
              <div className="contact-card contact-info-card">
                <div className="card-header">
                  <div className="card-icon contact-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21 8V21H3V8L12 2L21 8Z" stroke="#BBA984" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="3 8L12 14L21 8" stroke="#BBA984" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3>Contact Information</h3>
                </div>
                <div className="contact-emails">
                  <div className="email-item">
                    <div className="email-label">
                      <span className="email-bullet">•</span>
                      <strong>General Inquiries:</strong>
                    </div>
                    <a href="mailto:info@rayadgroup.com" className="email-link">info@rayadgroup.com</a>
                  </div>
                  <div className="email-item">
                    <div className="email-label">
                      <span className="email-bullet">•</span>
                      <strong>Investor Relations:</strong>
                    </div>
                    <a href="mailto:investor-relations@rayadgroup.com" className="email-link">investor-relations@rayadgroup.com</a>
                  </div>
                  <div className="email-item">
                    <div className="email-label">
                      <span className="email-bullet">•</span>
                      <strong>Media Inquiries:</strong>
                    </div>
                    <a href="mailto:media@rayadgroup.com" className="email-link">media@rayadgroup.com</a>
                  </div>
                </div>
              </div>

              <div className="contact-card contact-info-card">
                <div className="card-header">
                  <div className="card-icon contact-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 22s-8-7.5-8-13a8 8 0 1 1 16 0c0 5.5-8 13-8 13z" stroke="#BBA984" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="12" cy="9" r="2.5" stroke="#BBA984" strokeWidth="2"/>
                    </svg>
                  </div>
                  <h3>Office Address</h3>
                </div>
                <div className="contact-emails">
                  <div className="email-item">
                    <div className="email-label">
                      <strong>Rayad Group Technology FZ LLE</strong>
                    </div>
                    <p className="email-link" style={{ margin: 0 }}>
                      Office 2002, 20th Floor, Creative Tower,<br />
                      Fujairah, United Arab Emirates
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default Contact;